/**
 * CART DRAWER - Healing Sounds Custom
 * Gestion du panier latéral avec slide animation
 */

class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('cart-drawer');
    if (!this.drawer) return;
    
    this.overlay = this.drawer.querySelector('.cart-drawer__overlay');
    this.content = this.drawer.querySelector('.cart-drawer__content');
    this.form = this.drawer.querySelector('#cart-drawer-form');
    
    this.init();
  }

  init() {
    // Event listeners pour fermer le drawer
    const closeButtons = this.drawer.querySelectorAll('[data-action="close-drawer"]');
    closeButtons.forEach(button => {
      button.addEventListener('click', () => this.close());
    });

    // Fermer avec Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.getAttribute('aria-hidden') === 'false') {
        this.close();
      }
    });

    // Event listeners pour les boutons du header (ajout au panier)
    this.setupCartTriggers();

    // Event listeners pour les quantités
    this.setupQuantityControls();

    // Event listener pour les suppressions
    this.setupRemoveButtons();

    // Event listener pour le shipping protection toggle
    this.setupShippingProtection();

    // Event listeners pour les upsells
    this.setupUpsellsSlider();

    // Empêcher le scroll du body quand le drawer est ouvert
    this.preventBodyScroll();
  }

  setupCartTriggers() {
    // Empêcher le comportement par défaut sur tous les clics du panier
    document.body.addEventListener('click', (e) => {
      const cartButton = e.target.closest('.header__cart-toggle, [data-action="toggle-mini-cart"]');
      if (cartButton) {
        e.preventDefault();
        e.stopPropagation();
        this.open();
      }
    }, true);

    // Écouter UNIQUEMENT les événements d'ajout au panier
    // NE PAS écouter 'cart:refresh' qui se déclenche au chargement de page
    const cartEvents = ['cart:item-added', 'product:added', 'product-form:added'];
    
    cartEvents.forEach(eventName => {
      document.addEventListener(eventName, async (e) => {
        console.log(`📦 Événement détecté: ${eventName}`);
        
        // Délai pour laisser BOGO/apps tierces traiter leurs réductions
        // avant de rafraîchir le panier
        await this.waitForAppsToProcess();
        
        await this.refreshCart();
        this.open(); // Ouvrir SEULEMENT lors d'un vrai ajout
        // Mettre à jour le compteur d'articles
        const cart = await fetch('/cart.js').then(r => r.json());
        this.updateCartCount(cart.item_count);
      });
    });
  }

  setupQuantityControls() {
    const quantityButtons = this.drawer.querySelectorAll('[data-action="increase-quantity"], [data-action="decrease-quantity"]');
    
    quantityButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const line = button.dataset.line;
        const quantity = parseInt(button.dataset.quantity);
        
        this.updateQuantity(line, quantity);
      });
    });

    // Input direct
    const quantityInputs = this.drawer.querySelectorAll('.cart-drawer-qty__input');
    quantityInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        const line = input.dataset.line;
        const quantity = parseInt(input.value);
        
        if (quantity > 0) {
          this.updateQuantity(line, quantity);
        }
      });
    });
  }

  setupRemoveButtons() {
    const removeButtons = this.drawer.querySelectorAll('[data-action="remove-item"]');
    
    removeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const line = button.dataset.line;
        this.updateQuantity(line, 0);
      });
    });
  }

  setupShippingProtection() {
    const toggle = this.drawer.querySelector('[data-action="toggle-protection"]');
    
    if (toggle) {
      const variantId = toggle.dataset.variantId;
      
      // Auto-add seulement au PREMIER chargement de la page (pas après refresh)
      // On utilise sessionStorage pour tracker si on a déjà fait l'auto-add
      const autoAddKey = `protection_auto_added_${variantId}`;
      if (!sessionStorage.getItem(autoAddKey)) {
        sessionStorage.setItem(autoAddKey, 'true');
        this.ensureProtectionInCart(variantId);
      }
      
      // Éviter les doublons d'event listeners
      if (toggle.dataset.listenerAttached) return;
      toggle.dataset.listenerAttached = 'true';
      
      toggle.addEventListener('change', async (e) => {
        const isChecked = toggle.checked;
        
        // Désactiver le toggle pendant le traitement
        toggle.disabled = true;
        
        try {
          if (isChecked) {
            // Ajouter le produit de protection au panier
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({
                id: variantId,
                quantity: 1
              })
            });
          } else {
            // Retirer le produit de protection du panier
            await fetch('/cart/update.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({
                updates: {
                  [variantId]: 0
                }
              })
            });
          }
          
          // Rafraîchir le panier
          await this.refreshCart();
          
          // Mettre à jour le compteur
          const cart = await fetch('/cart.js').then(r => r.json());
          this.updateCartCount(cart.item_count);
          
        } catch (error) {
          console.error('Erreur toggle protection:', error);
          // Remettre le toggle à son état précédent
          toggle.checked = !isChecked;
        } finally {
          toggle.disabled = false;
        }
      });
    }
  }

  async ensureProtectionInCart(variantId) {
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      const alreadyInCart = cart.items.some(item => item.variant_id == variantId);
      
      if (!alreadyInCart) {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1
          })
        });
        
        // Rafraîchir le panier silencieusement
        await this.refreshCart();
        
        // Mettre à jour le compteur
        const updatedCart = await fetch('/cart.js').then(r => r.json());
        this.updateCartCount(updatedCart.item_count);
      }
    } catch (error) {
      console.error('Erreur auto-add protection:', error);
    }
  }

  setupUpsellsSlider() {
    const upsellsSection = this.drawer.querySelector('.cart-drawer__upsells');
    if (!upsellsSection) return;
    
    const track = upsellsSection.querySelector('.cart-drawer__upsells-track');
    const prevBtn = upsellsSection.querySelector('.cart-drawer__upsells-arrow--prev');
    const nextBtn = upsellsSection.querySelector('.cart-drawer__upsells-arrow--next');
    const cards = track?.querySelectorAll('.cart-drawer__upsell-card');
    
    if (!cards || cards.length === 0) {
      // Cacher la section si pas de produits
      upsellsSection.style.display = 'none';
      return;
    }
    
    // Navigation par scroll
    const scrollAmount = 250; // Largeur carte + gap
    
    // Flèche précédente
    prevBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      track.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    }, true);
    
    // Flèche suivante
    nextBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }, true);
    
    // Boutons ADD - empêcher propagation vers Lasso
    const addButtons = track?.querySelectorAll('[data-action="add-upsell"]');
    addButtons?.forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        const variantId = btn.dataset.variantId;
        
        btn.disabled = true;
        const originalText = btn.textContent;
        btn.textContent = '...';
        
        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              id: variantId,
              quantity: 1
            })
          });
          
          // Rafraîchir le panier (le produit disparaîtra du slider)
          await this.refreshCart();
          
          // Mettre à jour le compteur
          const cart = await fetch('/cart.js').then(r => r.json());
          this.updateCartCount(cart.item_count);
          
        } catch (error) {
          console.error('Erreur ajout upsell:', error);
          btn.disabled = false;
          btn.textContent = originalText;
        }
      }, true);
    });
  }

  /**
   * Attendre que les apps tierces (BOGO, bundles, etc.) aient fini de traiter
   * Utilise un polling pour détecter quand le panier est stable
   */
  async waitForAppsToProcess() {
    return new Promise(async (resolve) => {
      // Attendre un minimum de 400ms pour laisser les apps réagir
      await new Promise(r => setTimeout(r, 300));
      
      // Puis vérifier que le panier est stable (pas de changements pendant 200ms)
      let previousCart = await fetch('/cart.js').then(r => r.json());
      let stableCount = 0;
      const maxChecks = 4;
      
      const checkStability = async () => {
        await new Promise(r => setTimeout(r, 150));
        const currentCart = await fetch('/cart.js').then(r => r.json());
        
        // Comparer le nombre d'items et le total
        if (currentCart.item_count === previousCart.item_count && 
            currentCart.total_price === previousCart.total_price) {
          stableCount++;
          if (stableCount >= 1) {
            // Panier stable depuis 1 check
            resolve();
            return;
          }
        } else {
          // Panier a changé, reset le compteur
          stableCount = 0;
          previousCart = currentCart;
        }
        
        if (stableCount < 1 && stableCount < maxChecks) {
          await checkStability();
        } else {
          resolve();
        }
      };
      
      await checkStability();
    });
  }

  preventBodyScroll() {
    // Observer pour détecter l'ouverture/fermeture
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'aria-hidden') {
          const isHidden = this.drawer.getAttribute('aria-hidden') === 'true';
          
          if (isHidden) {
            document.body.style.overflow = '';
            document.body.classList.remove('drawer-open'); // Retirer la classe mobile
          } else {
            document.body.style.overflow = 'hidden';
            document.body.classList.add('drawer-open'); // Ajouter la classe mobile
          }
        }
      });
    });

    observer.observe(this.drawer, { attributes: true });
  }

  open() {
    this.drawer.setAttribute('aria-hidden', 'false');
    
    // Note: Le discount code est maintenant géré par Shopify nativement
    
    // Focus trap
    setTimeout(() => {
      const firstFocusable = this.drawer.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (firstFocusable) firstFocusable.focus();
    }, 300);
  }

  close() {
    this.drawer.setAttribute('aria-hidden', 'true');
  }

  async updateQuantity(line, quantity) {
    try {
      // Afficher un loading state sur l'item
      const item = this.drawer.querySelector(`[data-line="${line}"]`);
      if (item) {
        item.style.opacity = '0.5';
        item.style.pointerEvents = 'none';
      }

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          line: line,
          quantity: quantity
        })
      });

      if (!response.ok) throw new Error('Erreur lors de la mise à jour du panier');

      const cart = await response.json();
      
      // Rafraîchir le drawer complet
      await this.refreshCart();
      
      // Mettre à jour le compteur dans le header
      this.updateCartCount(cart.item_count);

      // Dispatch event pour autres scripts
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));

    } catch (error) {
      console.error('Erreur mise à jour panier:', error);
      alert('Une erreur est survenue. Veuillez réessayer.');
      
      // Retirer le loading state
      const item = this.drawer.querySelector(`[data-line="${line}"]`);
      if (item) {
        item.style.opacity = '';
        item.style.pointerEvents = '';
      }
    }
  }

  async refreshCart() {
    try {
      // Forcer un timestamp pour éviter le cache et récupérer la dernière version
      const timestamp = new Date().getTime();
      const url = `${window.location.pathname}?t=${timestamp}`;
      
      // Récupérer le HTML mis à jour du drawer
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'text/html',
          'Cache-Control': 'no-cache, no-store, must-revalidate'
        }
      });

      if (!response.ok) throw new Error('Erreur lors du rafraîchissement');

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Extraire le nouveau drawer
      const newDrawer = doc.getElementById('cart-drawer');
      if (newDrawer) {
        // Sauvegarder le code discount si présent
        const savedDiscountCode = sessionStorage.getItem('cart_discount_code');
        
        // Remplacer le contenu
        this.drawer.innerHTML = newDrawer.innerHTML;
        
        // Réinitialiser les event listeners
        this.overlay = this.drawer.querySelector('.cart-drawer__overlay');
        this.content = this.drawer.querySelector('.cart-drawer__content');
        this.form = this.drawer.querySelector('#cart-drawer-form');
        
        this.setupQuantityControls();
        this.setupRemoveButtons();
        this.setupShippingProtection();
        this.setupUpsellsSlider();
        
        const closeButtons = this.drawer.querySelectorAll('[data-action="close-drawer"]');
        closeButtons.forEach(button => {
          button.addEventListener('click', () => this.close());
        });
      }

      // Récupérer aussi le cart count pour mettre à jour
      const cart = await fetch('/cart.js').then(r => r.json());
      this.updateCartCount(cart.item_count);

    } catch (error) {
      console.error('Erreur rafraîchissement panier:', error);
    }
  }

  updateCartCount(count) {
    // Mettre à jour le compteur dans le header
    const cartCount = document.querySelector('.header__cart-count');
    if (cartCount) {
      cartCount.textContent = count;
      
      // Afficher/masquer selon le compte
      if (count > 0) {
        cartCount.style.display = '';
      } else {
        cartCount.style.display = 'none';
      }
    }

    // Mettre à jour le titre du drawer
    const drawerTitle = this.drawer.querySelector('.cart-drawer__title');
    if (drawerTitle) {
      const titleText = drawerTitle.textContent.replace(/\(\d+\)/, `(${count})`);
      drawerTitle.textContent = titleText;
    }
  }

}

// Initialiser quand le DOM est prêt
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartDrawer();
  });
} else {
  new CartDrawer();
}

// Export pour utilisation externe
window.CartDrawer = CartDrawer;

/**
 * HELPER: Intercepter les formulaires d'ajout au panier
 * pour déclencher l'ouverture du drawer
 */
document.addEventListener('submit', async (e) => {
  const form = e.target;
  
  // Vérifier si c'est un formulaire d'ajout au panier
  if (form.action && form.action.includes('/cart/add')) {
    e.preventDefault();
    
    try {
      const formData = new FormData(form);
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('Erreur ajout panier');

      const item = await response.json();
      
      // Dispatch event
      document.dispatchEvent(new CustomEvent('cart:item-added', { detail: item }));

    } catch (error) {
      console.error('Erreur ajout au panier:', error);
      alert('Une erreur est survenue lors de l\'ajout au panier.');
    }
  }
});

